import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Button, Divider, Grid, IconButton, Paper, Popper, TextField, Tooltip } from "@mzc-pdc/ui";
import { IconClose } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import moment from "moment/moment";
import { Formik } from "formik";
import { UsersInputV2 } from "../users-input";
import TagsInput from "../tags-input/tags-input";
import { BasicDatePicker } from "../datepicker/datepicker";
import FileMetaAttributions from "@components_v2/file-meta-attributions/file-meta-attributions";
import { Autocomplete } from "@components_v2/autocomplete";
import DurationField from "@components_v2/duration-field/duration-field";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import SearchAttributionsInput from "@features/attributions-input/search-attributions-input";
import Categories from "@routes/categories";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { BasicDateTimePicker } from "@features/datepicker/dateTimepicker";
import ProjectsInput from "@features/projects-input/projects-input";
import { TABLE_LIST_TYPE } from "@constants";
import { getRoleActions } from "@modules/audits-v2";
import PanelPropertyTitle from "@components/view/components/panel-property-title";

export const SearchPopover = ({
    type,
    open,
    scope,
    anchorEl,
    optionValues,
    getSearchFields,
    currentCategoryGroup,
    onClickClose,
    searchQueryParams,
    onClickSubmit,
    onClickReset,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isClear, setIsClear] = useState(false);
    const paramRef = useRef(null);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const [searchFields, setSearchFields] = useState([]);

    const handleChangeValue = (key, value) => {
        if (getCustomAttributionKey() === key) {
            if (key === "domain") {
                const newSearchFields = getSearchFields({ [key]: value });
                setSearchFields(newSearchFields);
                newSearchFields.map((el) =>
                    el.customAttributions?.map((a) => {
                        paramRef.current.setFieldValue([a.id], undefined);
                    }),
                );
                dispatch(
                    getRoleActions({
                        params: {
                            domain: value,
                            methods: ["POST", "PATCH", "DELETE", "PUT"].join(","),
                        },
                    }),
                );
            }
        }
        paramRef.current.setFieldValue(key, value);
    };

    const onFormikSubmit = useCallback(
        (values, actions) => {
            if (onClickSubmit) {
                onClickSubmit(values);
            }
            actions.setSubmitting(false);
            onClickClose();
        },
        [onClickSubmit, searchFields],
    );

    useEffect(() => {
        const defaultFields = getSearchFields();
        setSearchFields(defaultFields);
    }, []);

    const getCustomAttributionKey = () => {
        switch (type) {
            case TABLE_LIST_TYPE.AUDIT_LOGS:
                return "domain";
            default:
                return undefined;
        }
    };

    const getAttributionsComponent = useCallback(
        ({ param, values, isCustomAttribution }) => {
            return (
                <Grid
                    container
                    flexDirection={param.id === "fileMeta" ? "column" : "row"}
                    alignItems={"start"}
                    sx={{
                        marginTop: `16px`,
                        "&:first-of-type": { marginTop: 0 },
                    }}
                >
                    <Grid
                        item
                        sx={{
                            width: `160px`,
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <PanelPropertyTitle
                            variant={"body1_500"}
                            subtitle={
                                isCustomAttribution
                                    ? param.subtitle
                                    : t(`common::label::${param.subtitle}`, param.subtitle)
                            }
                            tooltip={param.description}
                        />
                    </Grid>
                    <Grid item flex={1} sx={{ width: "100%" }}>
                        {getValueByParam(values, param)}
                    </Grid>
                </Grid>
            );
        },
        [searchQueryParams, isClear, stage, project, optionValues],
    );

    const getValueByParam = useCallback(
        (values, param) => {
            const { id, type, isCustomField, value, options, multiple, placeholder, disabled } = param;
            if (!type) return;

            if (isCustomField) {
                return (
                    <MetadataFieldEditMode
                        id={id}
                        type={type}
                        value={values[id]}
                        options={options.map((option) => ({ ...option, selected: values[id]?.includes(option.value) }))}
                        onChangeField={(id, value) => {
                            handleChangeValue(id, value);
                        }}
                        showValidationError={false}
                    />
                );
            }

            switch (type) {
                case "text":
                    return (
                        <TextField
                            sx={{ width: "100%" }}
                            value={values[id] ?? ""}
                            placeholder={placeholder}
                            onChange={(e) => {
                                handleChangeValue(id, e.target.value);
                            }}
                        ></TextField>
                    );
                case "dateRange":
                    return (
                        <Grid container flexDirection={"row"} gap={1} sx={{ justifyContent: "space-between" }}>
                            <BasicDatePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::Start`, `Start`)}
                                value={values[id]?.start && moment(values[id]?.start)?.format("YYYY-MM-DD")}
                                maxDate={values[id]?.end && moment(values[id]?.end)?.format("YYYY-MM-DD")}
                                onChangeValue={(value) => {
                                    if (value?.format("YYYY-MM-DD")) {
                                        const _value = {
                                            ...values[id],
                                            start: value.startOf("day").toISOString(),
                                        };
                                        handleChangeValue(id, _value);
                                    } else {
                                        const _value = {
                                            ...values[id],
                                            start: null,
                                        };
                                        handleChangeValue(id, _value);
                                    }
                                }}
                            ></BasicDatePicker>
                            <BasicDatePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::End`, `End`)}
                                value={values[id]?.end && moment(values[id]?.end)?.format("YYYY-MM-DD")}
                                minDate={values[id]?.start && moment(values[id]?.start)?.format("YYYY-MM-DD")}
                                onChangeValue={(value) => {
                                    if (value?.format("YYYY-MM-DD")) {
                                        const _value = {
                                            ...values[id],
                                            end: value.endOf("day").toISOString(),
                                        };
                                        handleChangeValue(id, _value);
                                    } else {
                                        const _value = {
                                            ...values[id],
                                            end: null,
                                        };
                                        handleChangeValue(id, _value);
                                    }
                                }}
                            ></BasicDatePicker>
                        </Grid>
                    );
                case "date":
                    return (
                        <Grid container flexDirection={"row"} gap={1} sx={{ justifyContent: "space-between" }}>
                            <BasicDatePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::Start`, `Start`)}
                                value={values[`${id}StartAt`] && moment(values[`${id}StartAt`])?.format("YYYY-MM-DD")}
                                maxDate={values[`${id}EndAt`] && moment(values[`${id}EndAt`])?.format("YYYY-MM-DD")}
                                onChangeValue={(value) => {
                                    if (value?.format("YYYY-MM-DD")) {
                                        handleChangeValue(`${id}StartAt`, value.startOf("day").toISOString());
                                    } else handleChangeValue(`${id}StartAt`, null);
                                }}
                            ></BasicDatePicker>
                            <BasicDatePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::End`, `End`)}
                                value={values[`${id}EndAt`] && moment(values[`${id}EndAt`])?.format("YYYY-MM-DD")}
                                minDate={values[`${id}StartAt`] && moment(values[`${id}StartAt`])?.format("YYYY-MM-DD")}
                                onChangeValue={(value) => {
                                    if (value?.format("YYYY-MM-DD"))
                                        handleChangeValue(`${id}EndAt`, value.endOf("day").toISOString());
                                    else handleChangeValue(`${id}EndAt`, null);
                                }}
                            ></BasicDatePicker>
                        </Grid>
                    );
                case "dateTimeRange":
                    return (
                        <Grid
                            className={"BasicDateTimePicker"}
                            container
                            flexDirection={"row"}
                            gap={1}
                            sx={{ justifyContent: "space-between" }}
                        >
                            <BasicDateTimePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::Start`, `Start`)}
                                value={values[`${id}From`] ?? undefined}
                                maxDate={values[`${id}To`] ?? undefined}
                                onChangeValue={(value) => {
                                    if (value?.isValid()) handleChangeValue(`${id}From`, value.toISOString());
                                    else handleChangeValue(`${id}From`, null);
                                }}
                            ></BasicDateTimePicker>
                            <BasicDateTimePicker
                                sx={{ width: "49%" }}
                                label={t(`common::label::End`, `End`)}
                                value={values[`${id}To`] ?? undefined}
                                minDate={values[`${id}From`] ?? undefined}
                                onChangeValue={(value) => {
                                    if (value?.isValid()) handleChangeValue(`${id}To`, value.toISOString());
                                    else handleChangeValue(`${id}To`, null);
                                }}
                            ></BasicDateTimePicker>
                        </Grid>
                    );
                case "tags":
                    return (
                        <TagsInput
                            tags={values[id]?.map((v) => ({ value: v }))}
                            isClear={isClear}
                            onDeleteTag={(index) => {
                                handleChangeValue(
                                    id,
                                    values[id]?.filter((t, idx) => idx !== index).map(({ value }) => value),
                                );
                            }}
                            onAddTag={(tag) => {
                                handleChangeValue(id, values[id] ? values[id].concat(tag?.value) : [tag?.value]);
                            }}
                            isEdit={false}
                        />
                    );
                case "users":
                    return (
                        <UsersInputV2
                            user={{ id: values[id], name: values[`${id}Name`] }}
                            placeholder={placeholder}
                            isClear={isClear}
                            onDeleteUser={() => {
                                handleChangeValue(`${id}Name`, null);
                                handleChangeValue(id, null);
                            }}
                            onAddUser={(user) => {
                                handleChangeValue(`${id}Name`, user.name);
                                handleChangeValue(id, user.id);
                            }}
                            disabledSearchUsers={false}
                            isEdit={false}
                            scope={scope}
                        />
                    );
                case "select":
                    return (
                        <Autocomplete
                            id={id}
                            multiple={multiple}
                            disableClearable={multiple}
                            disableCloseOnSelect={multiple}
                            value={values[id] ? values[id] : undefined}
                            options={optionValues[id]?.filter(({ value }) => value) ?? []}
                            placeholder={placeholder}
                            onClickOption={(id, value) => handleChangeValue(id, value)}
                        />
                    );
                case "time": {
                    const key = `${id[0].toUpperCase()}${id.slice(1)}`;

                    return (
                        <DurationField
                            id={key}
                            disabled={disabled}
                            formatChars={{ h: "([0-9]|h)", m: "([0-9]|m)", s: "([0-9]|s)" }}
                            mask={"hh:mm:ss"}
                            placeholder={"hh:mm:ss"}
                            value={{ start: values[`start${key}`], end: values[`end${key}`] }}
                            onChange={(id, value) =>
                                handleChangeValue(
                                    id,
                                    value
                                        .replaceAll(/(h|m|s)/g, "_")
                                        .replace(/^__/, "hh")
                                        .replace(/:__:/, ":mm:")
                                        .replace(/:__$/, ":ss"),
                                )
                            }
                        />
                    );
                }
                case "fileMeta":
                    return (
                        <FileMetaAttributions
                            attributions={values["fileMetaTemp"] ?? []}
                            onFileMetaUpdated={(fileMeta) => {
                                const fileMetaParam =
                                    fileMeta && fileMeta.organizedItems && fileMeta.organizedItems.length > 0
                                        ? JSON.stringify({
                                              items: fileMeta.organizedItems,
                                          })
                                        : undefined;
                                handleChangeValue("fileMetaTemp", fileMeta.tempItems);
                                handleChangeValue("fileMeta", fileMetaParam);
                            }}
                        />
                    );
                case "categories":
                    return (
                        <Categories
                            tooltipDisabled={false}
                            categoryGroups={false}
                            selectedCategoryGroup={{
                                id: currentCategoryGroup?.id,
                                label: currentCategoryGroup?.name,
                            }} //schema에서 제공받아야 함
                            placeholder={placeholder}
                            type={value?.categoryType}
                            selectedValues={values["categories"]?.map((c) => {
                                return {
                                    label: c.name,
                                    name: c.name,
                                    value: c.id,
                                    id: c.id,
                                };
                            })}
                            onChange={(options) => {
                                handleChangeValue(
                                    "categories",
                                    options.map((option) => ({ id: option.id, name: option.name })),
                                );
                            }}
                        />
                    );
                case "attributions":
                    return (
                        <SearchAttributionsInput
                            defaultValues={values["attributions"] ?? []}
                            onChangeAttributions={(attributions, validationErrors) => {
                                if (!Object.keys(validationErrors)?.length)
                                    handleChangeValue(
                                        "attributions",
                                        attributions.map(({ key, value }) => ({ key, value })),
                                    );
                            }}
                        />
                    );
                case "projects": {
                    const parsedNames = values[`${id}Names`] === "" ? [] : values[`${id}Names`]?.split(",");
                    const parseData =
                        values[`${id}Ids`] === ""
                            ? []
                            : values[`${id}Ids`]?.split(",")?.map((el, index) => {
                                  return { id: el, name: parsedNames[index] };
                              });
                    return (
                        <ProjectsInput
                            data={parseData ?? []}
                            isClear={isClear}
                            disabled={false}
                            placeholder={(!parseData || parseData?.length === 0) && placeholder}
                            onChange={(projects) => {
                                handleChangeValue(`${id}Ids`, projects.map((el) => el.id)?.join(","));
                                handleChangeValue(`${id}Names`, projects.map((el) => el.name)?.join(","));
                            }}
                            maxFields={5}
                        />
                    );
                }
                default:
                    return;
            }
        },
        [searchQueryParams, isClear, stage, project, optionValues],
    );

    return (
        <Formik
            innerRef={paramRef}
            initialValues={searchQueryParams.params}
            onSubmit={(values, actions) => {
                const newValues = { ...values, isQuickSearch: false, offset: 0 };
                onFormikSubmit(newValues, actions);
            }}
            enableReinitialize={true}
        >
            {({ values, handleSubmit, resetForm, handleChange }) => {
                return (
                    <>
                        <Popper
                            disablePortal={true}
                            role="search-popper"
                            open={open}
                            anchorEl={anchorEl}
                            placement={"bottom-start"}
                            sx={{
                                zIndex: "800",
                            }} //TODO: popover를 껐다 켜야 크기가 검색창과 맞게 됨
                        >
                            <Paper
                                sx={{
                                    width: anchorEl ? window.getComputedStyle(anchorEl).width : "100%",
                                    boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.16)`,
                                    border: `1px solid ${theme.palette.secondary[600]}`,
                                    boxSizing: `border-box`,
                                }}
                            >
                                <form
                                    onSubmit={(e) => {
                                        handleSubmit(e);
                                    }}
                                >
                                    <Grid container flexDirection={"row"} flexWrap={`nowrap`} sx={{ padding: "24px" }}>
                                        <Grid item flex={1}>
                                            <Grid
                                                container
                                                sx={{
                                                    overflow: "scroll",
                                                    maxHeight: `${
                                                        searchFields.reduce(
                                                            (acc, cur) =>
                                                                acc > 12 ? acc + cur.attributions?.length : 12,
                                                            0,
                                                        ) * 55
                                                    }px`,
                                                }}
                                            >
                                                {searchFields.map(({ attributions, customAttributions }, idx) => (
                                                    <>
                                                        {idx !== 0 && (
                                                            <Divider
                                                                flexItem
                                                                orientation={"horizontal"}
                                                                sx={{ width: "100%", mt: theme.spacing(2.5) }}
                                                            />
                                                        )}
                                                        {customAttributions?.map((param) =>
                                                            getAttributionsComponent({
                                                                param,
                                                                values,
                                                                isCustomAttribution: true,
                                                            }),
                                                        )}
                                                        {attributions?.map((param) =>
                                                            getAttributionsComponent({
                                                                param,
                                                                values,
                                                                isCustomAttribution: false,
                                                            }),
                                                        )}
                                                    </>
                                                ))}
                                            </Grid>
                                            <Divider sx={{ margin: "24px 0" }} />
                                            <Grid container justifyContent={"flex-end"}>
                                                <Button
                                                    variant={`text`}
                                                    onClick={(e) => {
                                                        const params = searchQueryParams.getSearchQueryParams({});

                                                        resetForm({
                                                            values: {},
                                                            isSubmitting: false,
                                                        });
                                                        setIsClear(true);
                                                        onClickReset();
                                                    }}
                                                    color={`grey`}
                                                    size={"medium"}
                                                >
                                                    {t(`common::label::Reset`, `Reset`)}
                                                </Button>
                                                <Button
                                                    sx={{ marginLeft: `6px` }}
                                                    variant={`contained`}
                                                    onClick={handleSubmit}
                                                    color={`primary`}
                                                    size={"medium"}
                                                >
                                                    {t(`common::label::Search`, `Search`)}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                        <Grid item sx={{ paddingLeft: `15px` }}>
                                            <Tooltip title={t(`common::label::Close`, `Close`)}>
                                                <IconButton circled size={"medium"} onClick={onClickClose}>
                                                    <IconClose size={16} htmlColor={`#3c3d4f`} />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Paper>
                        </Popper>
                    </>
                );
            }}
        </Formik>
    );
};

export default memo(SearchPopover);
